<template>
  <main>
    <Container class="mb-30 pt-30">
      <h1
        class="text-2xl md:text-5xl font-bold mb-30 text-primary-500 lg:mb-40"
      >
        {{ title }}
      </h1>

      <h2 class="text-xl md:text-2xl font-bold mb-20">
        {{ $texts('governmentResolutions.business', 'Geschäft') }}
      </h2>

      <!-- Business data table -->
      <GovernmentResolutionDataTable
        v-if="businessDataRows.length"
        :data-rows="businessDataRows"
        class="mb-40"
      />

      <h2 class="text-xl md:text-2xl font-bold mb-20">
        {{
          $texts(
            'governmentResolutions.coveredInTheFollowingMeetings',
            'Behandelt an den folgenden Sitzungen',
          )
        }}
      </h2>

      <!-- Agenda Meeting Tables -->
      <GovernmentResolutionAgendaMeetingTable
        v-for="agendaMeeting in agendaMeetings"
        :key="agendaMeeting.guid"
        :agenda-meeting="agendaMeeting"
        class="mb-40"
      />

      <div class="text-center">
        <NuxtLink :to="backToOverviewUrl" class="button is-prev">
          {{ $texts('backToOverview', 'Zurück zur Übersicht') }}
        </NuxtLink>
      </div>
    </Container>
  </main>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import type { DataTableRow, Hit } from '~/layers/government-resolutions/types'
import type {
  BreadcrumbFragment,
  GovernmentResolutionDetailPageFragment,
} from '#graphql-operations'

definePageMeta({
  name: 'government-resolutions-detail-page',
  title: 'abc',
  path: '/regierungsratsbeschluesse/:id',
  drupalRoute: false,
})

const nuxtRoute = useRoute()
const { $texts } = useNuxtApp()

const interceptor = useFetchInterceptor()

const { data } = await useFetch<Hit>(
  `/api/government-resolutions/${nuxtRoute.params.id}`,
  interceptor,
)

// If returned data is empty, then throw a 404 response.
if (!data.value || Object.keys(data.value).length === 0) {
  // Throw an error.
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
    fatal: true,
  })
}

const title = computed(() => data.value?.presidentialNumber || '')
const agendaMeetings = computed(() => data.value?.agendaMeetings || [])
const gribsUrl = computed(() => {
  const url = `http://www.grosserrat.bs.ch/suche/geschaefte/details/?idurl=${data.value?.presidentialNumber?.substring(
    1,
    3,
  )}.${data.value?.presidentialNumber?.substring(3, 7)}`
  return `<a href="${url}" target="_blank" class="text-primary-600 hover:underline pl-5">${$texts(
    'governmentResolutions.gribsUrlText',
    '[Grossratsinformationssystem - GRIBS]',
  )}</a>`
})

const businessDataRows = computed<DataTableRow[]>(() => {
  if (!data.value) {
    return []
  }

  const dataRows: DataTableRow[] = [
    {
      label: $texts(
        'governmentResolutions.presidentialNumber',
        'Präsidial-Nr.',
      ),
      value: data.value.presidentialNumber,
    },
    {
      label: $texts('title', 'Titel'),
      value: data.value.title,
    },
    {
      label: $texts('governmentResolutions.leadManagement', 'Federführung'),
      value: data.value.leadManagement,
    },
    {
      label: $texts('governmentResolutions.parliamentary', 'Parlamentarisch'),
      value:
        data.value.parliamentary === true
          ? $texts('yes', 'Ja') + gribsUrl.value
          : $texts('no', 'Nein'),
    },
  ]

  return dataRows
})

const backToOverviewUrl = computed<RouteLocationRaw>(() => {
  // If there is a back URL, then use that one for the button.
  if (
    'backUrl' in nuxtRoute.query &&
    nuxtRoute.query.backUrl !== '' &&
    typeof nuxtRoute.query.backUrl === 'string'
  ) {
    return nuxtRoute.query.backUrl
  }

  // Fallback route.
  return { name: 'government-resolutions-overview' }
})

const { data: parentNode } = await useAsyncGraphqlQuery(
  'governmentResolutionDetailPage',
  null,
  {
    transform: function (data): GovernmentResolutionDetailPageFragment | null {
      if (data.data.node && 'url' in data.data.node) {
        return data.data.node
      }
      return null
    },
    graphqlCaching: {
      client: true,
    },
  },
)

const breadcrumbs = computed<BreadcrumbFragment[]>(() => {
  const parentBreadcrumbs: BreadcrumbFragment[] =
    parentNode.value?.url && 'breadcrumb' in parentNode.value.url
      ? parentNode.value.url.breadcrumb
      : []

  return [
    ...parentBreadcrumbs.map((crumb, index) => {
      if (index === parentBreadcrumbs.length - 1) {
        return {
          ...crumb,
          isHidden: false,
          url: {
            path: parentNode.value?.url?.path || '',
          },
        }
      }

      return crumb
    }),
    {
      url: {
        path: nuxtRoute.path,
      },
      title: title.value,
      isHidden: true,
    },
  ]
})

setActiveMenuKey()
setBreadcrumbLinks(breadcrumbs.value)
await renderPageDependencies()
</script>
